import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowsClockwise,
  CaretDown,
  CaretRight,
  CheckCircle,
  Clock,
  Code,
  Copy,
  Database,
  Eye,
  EyeSlash,
  FloppyDisk,
  Gauge,
  GearSix,
  Info,
  Lightning,
  LinkSimple,
  Play,
  Trash,
  Warning,
  WarningCircle,
  X
} from '@phosphor-icons/react';
import { 
  useNodeTypeRegistry, 
  getNodeCategory, 
  getCategoryColors,
  getInputType,
  shouldShowParam
} from './useNodeTypeRegistry';

/**
 * NodeConfigPanelV2 - Registry-driven node configuration with Setup/Advanced tabs
 * 
 * Features:
 * - Setup tab: Core fields driven by node type registry
 * - Advanced tab: Reliability, Mapping, Diagnostics
 * - Dynamic param rendering based on params_schema
 * - Provider binding UI for capability.invoke nodes
 */

const NodeConfigPanelV2 = ({ 
  node, 
  onClose, 
  onUpdate, 
  onDelete, 
  onDuplicate,
  onTest,
  availableProviders = [],
  mcpTools = []
}) => {
  const { 
    getNodeType, 
    getSimpleFields, 
    getAdvancedFields, 
    getParamsSchema,
    supportsBinding,
    supportsMapping,
    canHaveSideEffects,
    loading: registryLoading 
  } = useNodeTypeRegistry();

  // Active tab state
  const [activeTab, setActiveTab] = useState('setup');
  
  // Local config state - initialized from node
  const [localConfig, setLocalConfig] = useState(() => 
    node?.data?.params || node?.data?.config || {}
  );
  const [localExec, setLocalExec] = useState(() => 
    node?.data?.exec || { enabled: true }
  );
  const [localBinding, setLocalBinding] = useState(() => 
    node?.data?.binding || null
  );
  const [localMapping, setLocalMapping] = useState(() => 
    node?.data?.mapping || null
  );
  const [hasChanges, setHasChanges] = useState(false);

  // Track node ID to reset state when node changes
  const [trackedNodeId, setTrackedNodeId] = useState(node?.id);
  
  // Reset local state when a different node is selected
  if (node?.id !== trackedNodeId) {
    setTrackedNodeId(node?.id);
    setLocalConfig(node?.data?.params || node?.data?.config || {});
    setLocalExec(node?.data?.exec || { enabled: true });
    setLocalBinding(node?.data?.binding || null);
    setLocalMapping(node?.data?.mapping || null);
    setHasChanges(false);
  }

  // Get node type definition
  // Priority: data.nodeType > resolved capability > node.type
  // For capability.invoke nodes, look up the actual capability from params or data
  const rawNodeType = useMemo(() => {
    // Collect the base capability ID from various sources
    let baseCapabilityId = null;
    
    // Check explicit nodeType in data
    if (node?.data?.nodeType) {
      baseCapabilityId = node.data.nodeType;
    }
    
    // For capability.invoke nodes, also check params/config for capability ID
    const reactFlowType = node?.type;
    if (!baseCapabilityId && (reactFlowType === 'capability.invoke' || reactFlowType === 'capability')) {
      baseCapabilityId = node?.data?.params?.capability || 
                        node?.data?.config?.capability ||
                        node?.data?.capability ||
                        node?.params?.capability;
    }
    
    // If we have a capability ID, generate all possible registry type names
    if (baseCapabilityId) {
      // The capability ID might be:
      // - Already fully qualified: 'action.send_email' 
      // - Just the action name: 'send_email'
      // We need to try multiple patterns to find the matching registry entry
      const possibleTypes = [
        baseCapabilityId,                           // exact match (might already have prefix)
        `action.${baseCapabilityId}`,               // action prefix
        `capability.${baseCapabilityId}`,           // capability prefix  
        `ai.${baseCapabilityId}`,                   // ai prefix
        `input.${baseCapabilityId}`,                // input prefix
        `output.${baseCapabilityId}`,               // output prefix
        `transform.${baseCapabilityId}`,            // transform prefix
        `logic.${baseCapabilityId}`,                // logic prefix
        `trigger.${baseCapabilityId}`,              // trigger prefix
      ];
      return possibleTypes;
    }
    
    return reactFlowType;
  }, [node?.data?.nodeType, node?.type, node?.data?.params?.capability, node?.data?.config?.capability, node?.data?.capability, node?.params?.capability]);
  
  // If we have multiple possible types, find the first one that exists in registry
  const nodeType = useMemo(() => {
    if (Array.isArray(rawNodeType)) {
      for (const t of rawNodeType) {
        const def = getNodeType(t);
        if (def) return t;
      }
      return rawNodeType[0]; // fallback to first option
    }
    return rawNodeType;
  }, [rawNodeType, getNodeType]);
  
  const nodeDef = useMemo(() => getNodeType(nodeType), [nodeType, getNodeType]);
  const paramsSchema = useMemo(() => getParamsSchema(nodeType), [nodeType, getParamsSchema]);
  const simpleFields = useMemo(() => getSimpleFields(nodeType), [nodeType, getSimpleFields]);
  const advancedFields = useMemo(() => getAdvancedFields(nodeType), [nodeType, getAdvancedFields]);

  // Category and colors
  const category = getNodeCategory(nodeType);
  const colors = getCategoryColors(category);

  // Handlers
  const handleParamChange = (key, value) => {
    setLocalConfig(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleExecChange = (key, value) => {
    setLocalExec(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleBindingChange = (key, value) => {
    setLocalBinding(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    const updatedData = {
      params: localConfig,
      config: localConfig, // Keep backward compatibility
      exec: localExec,
      binding: localBinding,
      mapping: localMapping
    };
    onUpdate?.(node.id, updatedData);
    setHasChanges(false);
  };

  if (!node) return null;

  // Loading state
  if (registryLoading) {
    return (
      <div className="w-80 h-full bg-[#0d0e12] border-l border-white/10 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ x: 300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 300, opacity: 0 }}
      className="w-80 h-full bg-[#0d0e12] border-l border-white/10 flex flex-col"
      data-testid="node-config-panel-v2"
    >
      {/* Header */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${colors.bgColor}`}>
              <Lightning size={16} className={colors.color} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">
                {nodeDef?.label || node.data?.label || node.data?.config?.label || 'Node Config'}
              </h3>
              <p className="text-xs text-slate-500 capitalize">
                {category === 'unknown' 
                  ? (node.data?.category || 'Custom Node')
                  : category.replace('_', ' ')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/5 text-slate-500 hover:text-white transition-colors"
            data-testid="close-node-config"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white/5 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('setup')}
            className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activeTab === 'setup'
                ? 'bg-indigo-500 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
            data-testid="tab-setup"
          >
            Setup
          </button>
          <button
            onClick={() => setActiveTab('advanced')}
            className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activeTab === 'advanced'
                ? 'bg-indigo-500 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
            data-testid="tab-advanced"
          >
            Advanced
          </button>
        </div>

        {/* Unsaved indicator */}
        {hasChanges && (
          <div className="flex items-center gap-1.5 text-xs text-amber-400 mt-2">
            <WarningCircle size={12} />
            Unsaved changes
          </div>
        )}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <AnimatePresence mode="wait">
          {activeTab === 'setup' ? (
            <motion.div
              key="setup"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="space-y-4"
            >
              {/* Node Label */}
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">Node Name</label>
                <input
                  type="text"
                  value={localConfig.label || node.data?.label || ''}
                  onChange={(e) => handleParamChange('label', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                    text-sm text-white focus:outline-none focus:border-indigo-500/50"
                  placeholder="Enter node name..."
                  data-testid="node-label-input"
                />
              </div>

              {/* Enabled Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white">Enabled</p>
                  <p className="text-xs text-slate-500">Execute this node in the flow</p>
                </div>
                <button
                  onClick={() => handleExecChange('enabled', !localExec.enabled)}
                  className={`relative w-10 h-5 rounded-full transition-colors ${
                    localExec.enabled !== false ? 'bg-indigo-500' : 'bg-white/10'
                  }`}
                  data-testid="node-enabled-toggle"
                >
                  <span 
                    className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                      localExec.enabled !== false ? 'translate-x-5' : ''
                    }`}
                  />
                </button>
              </div>

              {/* Provider Binding (for capability.invoke) */}
              {supportsBinding(nodeType) && (
                <ProviderBindingSection
                  binding={localBinding}
                  onChange={handleBindingChange}
                  availableProviders={availableProviders}
                  mcpTools={mcpTools}
                />
              )}

              {/* Dynamic Params from Schema */}
              {Object.entries(paramsSchema).map(([paramId, paramSchema]) => {
                // Only show simple visibility params in Setup tab
                if (paramSchema.visibility === 'advanced') return null;
                if (!shouldShowParam(paramSchema, localConfig)) return null;
                
                return (
                  <ParamField
                    key={paramId}
                    paramId={paramId}
                    schema={paramSchema}
                    value={localConfig[paramId]}
                    onChange={(value) => handleParamChange(paramId, value)}
                  />
                );
              })}

              {/* Side Effects Warning */}
              {canHaveSideEffects(nodeType) && (
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <Warning size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-300">
                    This node can have side effects (send emails, make API calls, etc.)
                  </p>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="advanced"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="space-y-4"
            >
              {/* Description */}
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">Description</label>
                <textarea
                  value={localConfig.description || ''}
                  onChange={(e) => handleParamChange('description', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                    text-sm text-white focus:outline-none focus:border-indigo-500/50 resize-none"
                  placeholder="What does this node do?"
                />
              </div>

              {/* Reliability Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Gauge size={12} />
                  Reliability Settings
                </div>

                {/* Timeout */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5">
                    Timeout (ms)
                    <span className="text-slate-600 ml-1">• Leave empty to inherit agent default</span>
                  </label>
                  <input
                    type="number"
                    value={localExec.timeout_ms || ''}
                    onChange={(e) => handleExecChange('timeout_ms', e.target.value ? parseInt(e.target.value) : null)}
                    min={1000}
                    max={300000}
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                      text-sm text-white focus:outline-none focus:border-indigo-500/50"
                    placeholder="30000"
                  />
                </div>

                {/* Retry Policy */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5">Retry Policy</label>
                  <select
                    value={localExec.retry?.mode || 'none'}
                    onChange={(e) => handleExecChange('retry', { 
                      ...localExec.retry, 
                      mode: e.target.value,
                      max_retries: e.target.value === 'none' ? 0 : (localExec.retry?.max_retries || 3)
                    })}
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                      text-sm text-white focus:outline-none focus:border-indigo-500/50"
                  >
                    <option value="none">No Retry</option>
                    <option value="fixed">Fixed Delay</option>
                    <option value="exponential">Exponential Backoff</option>
                  </select>
                </div>

                {localExec.retry?.mode && localExec.retry.mode !== 'none' && (
                  <div>
                    <label className="block text-xs text-slate-400 mb-1.5">Max Retries</label>
                    <input
                      type="number"
                      value={localExec.retry?.max_retries || 3}
                      onChange={(e) => handleExecChange('retry', { 
                        ...localExec.retry, 
                        max_retries: parseInt(e.target.value) 
                      })}
                      min={1}
                      max={10}
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                        text-sm text-white focus:outline-none focus:border-indigo-500/50"
                    />
                  </div>
                )}

                {/* Continue on Error */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localExec.continue_on_error || false}
                    onChange={(e) => handleExecChange('continue_on_error', e.target.checked)}
                    className="w-4 h-4 rounded border-white/20 bg-white/5 text-indigo-500 
                      focus:ring-indigo-500/50"
                  />
                  <span className="text-xs text-slate-300">Continue flow on error</span>
                </label>
              </div>

              {/* Mapping Section (for capability.invoke and output.webhook_response) */}
              {supportsMapping(nodeType) && (
                <MappingSection
                  mapping={localMapping}
                  onChange={setLocalMapping}
                  setHasChanges={setHasChanges}
                />
              )}

              {/* Diagnostics Section (read-only) */}
              {node.data?.meta?.last_run && (
                <DiagnosticsSection meta={node.data.meta} />
              )}

              {/* Advanced Params from Schema */}
              {Object.entries(paramsSchema).map(([paramId, paramSchema]) => {
                // Only show advanced visibility params here
                if (paramSchema.visibility !== 'advanced') return null;
                if (!shouldShowParam(paramSchema, localConfig)) return null;
                
                return (
                  <ParamField
                    key={paramId}
                    paramId={paramId}
                    schema={paramSchema}
                    value={localConfig[paramId]}
                    onChange={(value) => handleParamChange(paramId, value)}
                  />
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-white/10 space-y-2">
        <button
          onClick={handleSave}
          disabled={!hasChanges}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg
            bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium
            disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="save-node-config"
        >
          <FloppyDisk size={14} />
          Save Changes
        </button>

        {/* Test Node Button */}
        <button
          onClick={() => onTest?.(node)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg
            bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 
            text-emerald-400 text-sm font-medium transition-colors"
          data-testid="test-node-btn"
        >
          <Play size={14} />
          Test Node
        </button>

        <div className="flex gap-2">
          <button
            onClick={() => onDuplicate?.(node)}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg
              bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 
              text-xs font-medium transition-colors"
          >
            <Copy size={12} />
            Duplicate
          </button>
          <button
            onClick={() => onDelete?.(node.id)}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg
              bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 
              text-xs font-medium transition-colors"
            data-testid="delete-node-btn"
          >
            <Trash size={12} />
            Delete
          </button>
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Provider Binding Section for capability.invoke nodes
 */
const ProviderBindingSection = ({ binding, onChange, availableProviders, mcpTools }) => {
  const [showApiKey, setShowApiKey] = useState(false);

  return (
    <div className="space-y-3 p-3 rounded-lg bg-white/[0.02] border border-white/10">
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <LinkSimple size={12} />
        Provider Binding
      </div>

      {/* Capability ID */}
      <div>
        <label className="block text-xs text-slate-400 mb-1.5">Capability</label>
        <input
          type="text"
          value={binding?.capability_id || ''}
          onChange={(e) => onChange('capability_id', e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
            text-sm text-white focus:outline-none focus:border-indigo-500/50"
          placeholder="e.g., summarization"
          data-testid="capability-id-input"
        />
      </div>

      {/* Provider Type */}
      <div>
        <label className="block text-xs text-slate-400 mb-1.5">Provider Type</label>
        <select
          value={binding?.provider_type || 'internal'}
          onChange={(e) => onChange('provider_type', e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
            text-sm text-white focus:outline-none focus:border-indigo-500/50"
          data-testid="provider-type-select"
        >
          <option value="internal">Internal (Built-in)</option>
          <option value="oauth">OAuth Provider</option>
          <option value="mcp">MCP Server</option>
        </select>
      </div>

      {/* Provider ID (for oauth/mcp) */}
      {binding?.provider_type && binding.provider_type !== 'internal' && (
        <div>
          <label className="block text-xs text-slate-400 mb-1.5">
            {binding.provider_type === 'oauth' ? 'OAuth Connection' : 'MCP Server'}
          </label>
          <select
            value={binding?.provider_id || ''}
            onChange={(e) => onChange('provider_id', e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
              text-sm text-white focus:outline-none focus:border-indigo-500/50"
          >
            <option value="">Select provider...</option>
            {availableProviders
              .filter(p => p.type === binding.provider_type)
              .map(provider => (
                <option key={provider.id} value={provider.id}>
                  {provider.name}
                </option>
              ))}
          </select>
        </div>
      )}

      {/* MCP Tool Selection */}
      {binding?.provider_type === 'mcp' && binding?.provider_id && (
        <div>
          <label className="block text-xs text-slate-400 mb-1.5">MCP Tool</label>
          <select
            value={binding?.tool_name || ''}
            onChange={(e) => onChange('tool_name', e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
              text-sm text-white focus:outline-none focus:border-indigo-500/50"
          >
            <option value="">Select tool...</option>
            {mcpTools
              .filter(t => t.provider_id === binding.provider_id)
              .map(tool => (
                <option key={tool.name} value={tool.name}>
                  {tool.name}
                </option>
              ))}
          </select>
        </div>
      )}
    </div>
  );
};

/**
 * Mapping Section for data transformation
 */
const MappingSection = ({ mapping, onChange, setHasChanges }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="space-y-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-xs text-slate-400 hover:text-white transition-colors"
      >
        {expanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
        <Code size={12} />
        Data Mapping
      </button>

      {expanded && (
        <div className="p-3 rounded-lg bg-white/[0.02] border border-white/10 space-y-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Input Mapping</label>
            <textarea
              value={mapping?.input_map ? JSON.stringify(mapping.input_map, null, 2) : ''}
              onChange={(e) => {
                try {
                  const parsed = e.target.value ? JSON.parse(e.target.value) : null;
                  onChange({ ...mapping, input_map: parsed });
                  setHasChanges(true);
                } catch {
                  // Invalid JSON, don't update
                }
              }}
              rows={3}
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                text-xs text-white font-mono focus:outline-none focus:border-indigo-500/50 resize-none"
              placeholder='{"field": {"kind": "var", "value": "input.data"}}'
            />
          </div>

          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Output Mapping</label>
            <textarea
              value={mapping?.output_map ? JSON.stringify(mapping.output_map, null, 2) : ''}
              onChange={(e) => {
                try {
                  const parsed = e.target.value ? JSON.parse(e.target.value) : null;
                  onChange({ ...mapping, output_map: parsed });
                  setHasChanges(true);
                } catch {
                  // Invalid JSON, don't update
                }
              }}
              rows={3}
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                text-xs text-white font-mono focus:outline-none focus:border-indigo-500/50 resize-none"
              placeholder='{"result": {"kind": "var", "value": "output.data"}}'
            />
          </div>

          <div className="flex items-start gap-2 p-2 rounded bg-blue-500/10 border border-blue-500/20">
            <Info size={12} className="text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-[10px] text-blue-300">
              Use {"{{variable}}"} syntax for templates, or JSON for complex mappings.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Diagnostics Section (read-only runtime info)
 */
const DiagnosticsSection = ({ meta }) => {
  const lastRun = meta?.last_run;
  if (!lastRun) return null;

  const statusColors = {
    success: 'text-emerald-400 bg-emerald-500/10',
    error: 'text-red-400 bg-red-500/10',
    skipped: 'text-slate-400 bg-slate-500/10',
    pending: 'text-amber-400 bg-amber-500/10'
  };

  return (
    <div className="p-3 rounded-lg bg-white/[0.02] border border-white/10 space-y-2">
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <ArrowsClockwise size={12} />
        Last Execution
      </div>

      <div className="flex items-center justify-between">
        <span className={`px-2 py-0.5 rounded text-xs ${statusColors[lastRun.status] || statusColors.pending}`}>
          {lastRun.status}
        </span>
        {lastRun.latency_ms && (
          <span className="text-xs text-slate-500">{lastRun.latency_ms}ms</span>
        )}
      </div>

      {lastRun.error_message && (
        <p className="text-xs text-red-400 bg-red-500/10 p-2 rounded">
          {lastRun.error_message}
        </p>
      )}

      {meta?.schema_drift_detected && (
        <div className="flex items-center gap-2 text-xs text-amber-400">
          <Warning size={12} />
          Schema drift detected - tool schema may have changed
        </div>
      )}
    </div>
  );
};

/**
 * Dynamic Parameter Field based on schema
 */
const ParamField = ({ paramId, schema, value, onChange }) => {
  const inputType = getInputType(schema);

  const renderInput = () => {
    switch (inputType) {
      case 'checkbox':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) => onChange(e.target.checked)}
              className="w-4 h-4 rounded border-white/20 bg-white/5 text-indigo-500"
            />
            <span className="text-xs text-slate-300">{schema.label}</span>
          </label>
        );

      case 'select':
        return (
          <select
            value={value || schema.default || ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
              text-sm text-white focus:outline-none focus:border-indigo-500/50"
          >
            {!schema.required && <option value="">Select...</option>}
            {(schema.options || []).map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );

      case 'multi-select':
        return (
          <div className="space-y-1">
            {(schema.options || []).map(opt => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(value || []).includes(opt.value)}
                  onChange={(e) => {
                    const current = value || [];
                    if (e.target.checked) {
                      onChange([...current, opt.value]);
                    } else {
                      onChange(current.filter(v => v !== opt.value));
                    }
                  }}
                  className="w-4 h-4 rounded border-white/20 bg-white/5 text-indigo-500"
                />
                <span className="text-xs text-slate-300">{opt.label}</span>
              </label>
            ))}
          </div>
        );

      case 'textarea':
      case 'code':
      case 'json':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            rows={inputType === 'code' || inputType === 'json' ? 4 : 2}
            className={`w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
              text-sm text-white focus:outline-none focus:border-indigo-500/50 resize-none
              ${inputType === 'code' || inputType === 'json' ? 'font-mono text-xs' : ''}`}
            placeholder={schema.placeholder || ''}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value ?? schema.default ?? ''}
            onChange={(e) => onChange(e.target.value ? parseFloat(e.target.value) : null)}
            min={schema.min}
            max={schema.max}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
              text-sm text-white focus:outline-none focus:border-indigo-500/50"
            placeholder={schema.placeholder || ''}
          />
        );

      default:
        return (
          <input
            type={inputType}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
              text-sm text-white focus:outline-none focus:border-indigo-500/50"
            placeholder={schema.placeholder || ''}
          />
        );
    }
  };

  // Checkbox renders its own label
  if (inputType === 'checkbox') {
    return <div className="mt-2">{renderInput()}</div>;
  }

  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1.5">
        {schema.label}
        {schema.required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {renderInput()}
      {schema.description && (
        <p className="text-[10px] text-slate-500 mt-1">{schema.description}</p>
      )}
    </div>
  );
};

export default NodeConfigPanelV2;
